import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskTrackingService, { taskErrorCode, taskErrorMessage } from '../../services/TaskTrackingService';
import { confirmDiscard } from './confirmAccess';
import { planBody } from './startTracking';
import { contentFieldsError } from './taskFields';
import TaskFieldsSection, { emptyTaskFields } from './TaskFieldsSection';
import { fileWord, listNames } from './taskUi';

const TYPES = [
    { value: 'VIDEO', label: 'Video' },
    { value: 'TEXT', label: 'Text file' },
    { value: 'BOOK', label: 'Book' },
    { value: 'AUDIO', label: 'Dubbing audio' },
];

/**
 * New task: work that's planned before its file exists. One waiting task per language, which
 * stays out of everyone's My tasks until the file is linked. onCreated gets the first task's id,
 * because a manager on My tasks wouldn't otherwise see what they just made. Dubbing audio has no
 * file to wait for, so it starts at once.
 */
const NewTaskDialog = ({ open, onClose, onCreated }) => {
    useTrackUsage('NewTaskDialog', 'dialog', 'tasks', open);
    const [contentType, setContentType] = useState('VIDEO');
    const [title, setTitle] = useState('');
    const [fields, setFields] = useState(() => ({ ...emptyTaskFields(), track: true }));
    const [touched, setTouched] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setContentType('VIDEO');
        setTitle('');
        setFields({ ...emptyTaskFields(), track: true });
        setTouched(false);
        setError(null);
    }, [open]);

    const changeFields = (next) => {
        setTouched(true);
        setFields(next);
    };

    // Stages and the video-only fields don't carry over to another type
    const changeType = (value) => {
        setContentType(value);
        setFields((f) => ({ ...f, stageOverrides: {}, duration: '', sourceUrl: '', platform: '' }));
    };

    const close = async () => {
        if ((touched || title) && !(await confirmDiscard('Discard this new task?'))) return;
        onClose();
    };

    const send = (sameTitleConfirmed) =>
        TaskTrackingService.plan(planBody(contentType, title, fields, { sameTitleConfirmed }));

    const askSameTitle = async (languages) =>
        (
            await Swal.fire({
                title: 'A task with this title is already waiting',
                text: `${title.trim()} is waiting in ${listNames(languages)}. A second task gets its own shared folder. Create it anyway?`,
                showCancelButton: true,
                confirmButtonText: 'Create it',
                cancelButtonText: 'Go back',
            })
        ).isConfirmed;

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            let planned;
            try {
                planned = await send(false);
            } catch (e) {
                if (taskErrorCode(e) !== 'SAME_TITLE_WAITING') throw e;
                if (!(await askSameTitle(e.response.data.languages || []))) return;
                planned = await send(true);
            }
            onCreated(planned.taskIds[0]);
            onClose();
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const audio = contentType === 'AUDIO';
    const ready = title.trim() && fields.languages.length > 0 && !contentFieldsError(fields);

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>New task</DialogTitle>
            <DialogContent>
                <DialogContentText variant="body2" sx={{ mb: 1 }}>
                    {audio
                        ? 'For dubbed audio with no video. It starts now, the first person hears straight away, and the audio stays in its shared folder.'
                        : `For work that's planned before the file exists. Nobody is told yet. The first person hears once the ${fileWord(contentType)} is linked.`}
                </DialogContentText>
                {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                        select
                        margin="dense"
                        label="Type"
                        value={contentType}
                        onChange={(e) => changeType(e.target.value)}
                        sx={{ flex: '0 0 140px' }}
                    >
                        {TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                                {t.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        helperText={audio ? 'Names the task and its shared folder.' : "The file's name replaces this once it's linked."}
                        sx={{ flex: '1 1 240px' }}
                    />
                </Box>
                <TaskFieldsSection value={fields} onChange={changeFields} contentType={contentType} alwaysOn singleFile />
            </DialogContent>
            <DialogActions>
                <Button onClick={close}>Cancel</Button>
                <Button variant="contained" disabled={!ready || busy} onClick={submit}>
                    Create task
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewTaskDialog;
